import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../assets/utils/useAuth';
import { usePermissions } from '../assets/utils/usePermissions';
import { notification } from 'antd';
import {
  canAccess as canAccessModule,
  hasPermission as hasPerm,
  hasAnyPermission as hasAnyPerm,
  canStayOnModule,
} from '../assets/utils/permissions';
import { LEGACY_MODULE_ROLE_IDS } from '../assets/utils/accessCatalog';

/**
 * ProtectedRoute — permission-first gate with legacy staffRoleId soft fallback.
 * Wait while profile permissions hydrate; avoid /dashboard deny loops.
 */
export function ProtectedRoute({
  children,
  module,
  permission,
  permissions = [],
  fallback = '/dashboard',
  showError = true,
}) {
  const { user, permissions: authPermissions, profileBootstrapped } = useAuth();
  const { permissions: permissionMap, canAccess, hasPermission, hasAnyPermission } = usePermissions();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Wait for profile bootstrap so we don't flash-deny before hydration.
  // profileBootstrapped flips once the profile fetch RESOLVES (success or failure), so an
  // account whose profile call fails falls through to the legacy fallback instead of
  // spinning forever — and no user is redirected before the map has had its chance to load.
  const profileLoaded = user?.profile != null;
  const permissionsHydrating =
    !profileLoaded ||
    (user?.profile &&
      user.profile.permissions == null &&
      (!authPermissions || Object.keys(authPermissions).length === 0));

  if (permissionsHydrating && !profileBootstrapped) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  const hasGate = Boolean(module || permission || permissions.length > 0);
  const map = permissionMap && Object.keys(permissionMap).length > 0
    ? permissionMap
    : (user?.profile?.permissions
        ? Object.fromEntries(
            (Array.isArray(user.profile.permissions) ? user.profile.permissions : []).map((p) => [
              typeof p === 'string' ? p : p?.name,
              true,
            ])
          )
        : {});

  let allowed = !hasGate;
  if (hasGate) {
    allowed = true;
    if (module && !(canAccess(module) || canAccessModule(module, map))) allowed = false;
    if (allowed && permission && !(hasPermission(permission) || hasPerm(permission, map))) allowed = false;
    if (
      allowed &&
      permissions.length > 0 &&
      !(hasAnyPermission(permissions) || hasAnyPerm(permissions, map))
    ) {
      allowed = false;
    }

    // Soft fallback: legacy staffRoleId, and ONLY when the RBAC map is empty
    // (log_only / incomplete seed). canStayOnModule owns that guard — a populated map
    // must never be overridden by a legacy role id.
    // Permission-only gates get no legacy path: the only ones in the app are platform.*,
    // which is CLI-managed and was never meant to be legacy-reachable.
    if (!allowed && module) {
      allowed = canStayOnModule(module, map, user, LEGACY_MODULE_ROLE_IDS[module] || []);
    }
  }

  if (!allowed) {
    if (showError) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to access this page.",
        placement: 'topRight',
        duration: 3,
      });
    }
    // Avoid deny loops when fallback is also gated (e.g. /dashboard)
    const safeFallback = fallback === '/dashboard' && module === 'dashboard' ? '/login' : fallback;
    return <Navigate to={safeFallback} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
